"use client";

import * as React from "react";
import { useSession } from "next-auth/react";
import type {
  BlockBase,
  ContentStatus,
  ContentType,
} from "@/cms/blocks/registry/types";
import type { SaveContentBody } from "@/cms/api/dto";
import { toSlug } from "@/cms/lib/slug";
import {
  DEFAULT_CONTENT_LOCALE_VISIBILITY,
  getContentLocaleVisibility,
  normalizeArticleColor,
  withContentLocaleVisibility,
} from "@/cms/lib/contentPresentation";
import { useActiveEditorLock } from "@/hooks/useActiveEditorLock";
import type { TagOption } from "@/cms/editor/TagPickerPanel";
import { getLocalizedUserName } from "@/utils/userLocalization";
import type { AuthorSummary } from "@/cms/lib/authors";

type UsageEntry = {
  productId?: string | null;
  contentId?: string | null;
  fieldPath?: string | null;
};

const BLOCKS_BY_CONTENT_TYPE: Partial<
  Record<ContentType, Array<BlockBase["type"]>>
> = {
  PRODUCT_LIST: ["heading", "paragraph", "productCard"],
};

const toNullableId = (value: unknown): string | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  return null;
};

const normalizeUsageEntry = (usage: any): UsageEntry | null => {
  const contentId = toNullableId(usage?.contentId);
  const productId = toNullableId(usage?.productId);
  const fieldPath =
    typeof usage?.fieldPath === "string"
      ? usage.fieldPath.trim() || null
      : null;
  if (!contentId && !productId && !fieldPath) return null;
  return { contentId, productId, fieldPath };
};

const dedupeUsages = (usages: UsageEntry[]) => {
  const map = new Map<string, UsageEntry>();
  usages.forEach((usage) => {
    const key = `${usage.productId ?? ""}|${usage.contentId ?? ""}|${usage.fieldPath ?? ""}`;
    if (!map.has(key)) map.set(key, usage);
  });
  return Array.from(map.values());
};

const collectUsageMap = (params: {
  blocks: BlockBase[];
  locale: "en" | "fa";
  contentId?: string | null;
}): Map<string, UsageEntry[]> => {
  const { blocks, locale, contentId } = params;
  const contentIdVal = toNullableId(contentId);
  const map = new Map<string, UsageEntry[]>();
  if (!contentIdVal || !Array.isArray(blocks)) return map;

  const addUsage = (assetId: string | null | undefined, fieldPath: string) => {
    const normalizedId = toNullableId(assetId);
    if (!normalizedId || !fieldPath) return;
    const list = map.get(normalizedId) ?? [];
    list.push({ contentId: contentIdVal, fieldPath });
    map.set(normalizedId, list);
  };

  blocks.forEach((block) => {
    if (!block?.id) return;
    if (block.type === "image") {
      const assetId = (block.data as any)?.assetId;
      addUsage(assetId, `cms.${locale}.image.${block.id}`);
    } else if (block.type === "reference") {
      // reference assets are resolved centrally when saving content
    }
  });

  return map;
};

const buildContentUsageMap = (params: {
  blocks_en?: BlockBase[];
  blocks_fa?: BlockBase[];
  contentId?: string | null;
}): Map<string, UsageEntry[]> => {
  const en = collectUsageMap({
    blocks: params.blocks_en ?? [],
    locale: "en",
    contentId: params.contentId ?? null,
  });
  const fa = collectUsageMap({
    blocks: params.blocks_fa ?? [],
    locale: "fa",
    contentId: params.contentId ?? null,
  });
  const merged = new Map<string, UsageEntry[]>();
  [en, fa].forEach((map) => {
    map.forEach((value, key) => {
      const existing = merged.get(key) ?? [];
      merged.set(key, [...existing, ...value]);
    });
  });
  return merged;
};

const normalizeContent = (payload: any) => ({
  ...payload,
  tags: Array.isArray(payload?.tags) ? payload.tags : [],
  authorFaId:
    typeof payload?.authorFaId === "string" ? payload.authorFaId : null,
  authorEnId:
    typeof payload?.authorEnId === "string" ? payload.authorEnId : null,
  authorFa: payload?.authorFa ?? null,
  authorEn: payload?.authorEn ?? null,
  displayDate_draft: payload?.displayDate_draft ?? payload?.displayDate ?? null,
  readingMinutes_draft:
    typeof payload?.readingMinutes_draft === "string"
      ? payload.readingMinutes_draft
      : typeof payload?.readingMinutes_draft === "number"
        ? `${payload.readingMinutes_draft}min`
        : null,
  description_en_draft:
    payload?.description_en_draft ?? payload?.description_en ?? null,
  description_fa_draft:
    payload?.description_fa_draft ?? payload?.description_fa ?? null,
  articleColor_draft:
    normalizeArticleColor(
      payload?.articleColor_draft ?? payload?.articleColor ?? null,
    ) ?? null,
  articleColor_published:
    normalizeArticleColor(payload?.articleColor_published ?? null) ?? null,
  meta_draft: withContentLocaleVisibility(
    payload?.meta_draft ?? payload?.meta ?? {},
    getContentLocaleVisibility(payload?.meta_draft ?? payload?.meta ?? {}),
  ),
});

type UseContentEditorParams = {
  contentId: string | null;
  fallbackPresetType?: string | null;
  onUpdated?: (payload: { id: string; data: any }) => void;
};

export function useContentEditor({
  contentId,
  fallbackPresetType = null,
  onUpdated,
}: UseContentEditorParams) {
  const [content, setContent] = React.useState<any | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [errorIssues, setErrorIssues] = React.useState<
    Array<{ blockId?: string; field?: string; message: string }> | null
  >(null);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [statusAction, setStatusAction] = React.useState<
    "publish" | "unpublish" | "discard" | null
  >(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const lastContentIdRef = React.useRef<string | null>(null);

  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const locale =
    typeof document !== "undefined" && document.documentElement.lang === "fa"
      ? "fa"
      : "en";
  const userName =
    (session?.user
      ? getLocalizedUserName(locale, session.user).fullName
      : "") ||
    session?.user?.email ||
    "Unknown";
  const actorEmail = session?.user?.email ?? undefined;

  const activeContentId = content?.id ?? contentId;
  const shouldLock = Boolean(activeContentId && userId);
  const {
    status: lockStatus,
    lockedBy,
    retry: retryLock,
  } = useActiveEditorLock({
    activePath: activeContentId ? `cms/${activeContentId}` : null,
    userId,
    userName,
    userEmail: session?.user?.email ?? null,
    enabled: shouldLock,
  });
  const lockBlocked =
    shouldLock && lockStatus !== "active" && lockStatus !== "idle";

  const buildInitialContent = React.useCallback(
    () =>
      normalizeContent({
        id: null,
        type: fallbackPresetType ?? null,
        status: "draft",
        slug_en_draft: null,
        slug_fa_draft: null,
        title_en_draft: "",
        title_fa_draft: "",
        blocks_en_draft: [],
        blocks_fa_draft: [],
        meta_draft: {
          visibility: DEFAULT_CONTENT_LOCALE_VISIBILITY,
        },
        displayDate_draft: null,
        readingMinutes_draft: null,
        articleColor_draft: null,
        articleColor_published: null,
        description_en_draft: null,
        description_fa_draft: null,
        authorFaId: null,
        authorEnId: null,
        authorFa: null,
        authorEn: null,
        tags: [],
      }),
    [fallbackPresetType],
  );

  React.useEffect(() => {
    if (lastContentIdRef.current === contentId) return;
    lastContentIdRef.current = contentId;
  }, [contentId]);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setContent(null);
    if (!contentId) {
      setContent(buildInitialContent());
      setIsDirty(false);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const res = await fetch(`/api/cms/content/${contentId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || data?.error || "Load failed");
        if (!cancelled) {
          setContent(normalizeContent(data.content));
          setIsDirty(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Failed to load content");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildInitialContent, contentId]);

  React.useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const resolvedType = (content?.type ?? fallbackPresetType ?? null) as
    | ContentType
    | null;
  const isPublished = content?.status === "published";
  const isArchived = content?.status === "archived";

  const usageMap = React.useMemo(
    () =>
      buildContentUsageMap({
        blocks_en: content?.blocks_en_draft ?? [],
        blocks_fa: content?.blocks_fa_draft ?? [],
        contentId: content?.id ?? contentId ?? null,
      }),
    [content?.blocks_en_draft, content?.blocks_fa_draft, content?.id, contentId],
  );

  const syncContentAssetUsages = React.useCallback(
    async (params: { next: any; prev?: any }) => {
      const next = params.next ?? null;
      const prev = params.prev ?? null;
      const nextId = toNullableId(next?.id ?? prev?.id);
      if (!nextId) return true;

      const nextMap = buildContentUsageMap({
        blocks_en: next?.blocks_en_draft ?? [],
        blocks_fa: next?.blocks_fa_draft ?? [],
        contentId: nextId,
      });
      const prevMap = buildContentUsageMap({
        blocks_en: prev?.blocks_en_draft ?? [],
        blocks_fa: prev?.blocks_fa_draft ?? [],
        contentId: nextId,
      });

      const currentAssetIds = new Set(nextMap.keys());
      const prevAssetIds = new Set(prevMap.keys());
      const removedAssetIds = [...prevAssetIds].filter(
        (assetId) => !currentAssetIds.has(assetId),
      );

      const fetchExistingUsages = async (assetId: string) => {
        try {
          const res = await fetch(`/api/media/assets/${assetId}/usages`, {
            cache: "no-store",
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || data?.error);
          const usages = Array.isArray(data?.usages) ? data.usages : [];
          return usages
            .map(normalizeUsageEntry)
            .filter((u: UsageEntry): u is UsageEntry => Boolean(u));
        } catch (err) {
          console.error(`Failed to load usages for asset ${assetId}`, err);
          return [];
        }
      };

      const postUsages = async (assetId: string, usages: UsageEntry[]) => {
        const res = await fetch(`/api/media/assets/${assetId}/usages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ usages }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data?.message || data?.error || "Failed to update usages.",
          );
        }
      };

      const updateTasks = Array.from(nextMap.entries()).map(
        async ([assetId, contentUsages]) => {
          const existing = await fetchExistingUsages(assetId);
          const merged = dedupeUsages([
            ...existing.filter(
              (usage: UsageEntry) => usage.contentId !== nextId,
            ),
            ...contentUsages,
          ]);
          await postUsages(assetId, merged);
        },
      );

      const clearTasks = removedAssetIds.map(async (assetId) => {
        const existing = await fetchExistingUsages(assetId);
        const remaining = dedupeUsages(
          existing.filter((usage: UsageEntry) => usage.contentId !== nextId),
        );
        await postUsages(assetId, remaining);
      });

      const results = await Promise.allSettled([...updateTasks, ...clearTasks]);
      return results.every((result) => result.status === "fulfilled");
    },
    [],
  );

  const buildPayload = React.useCallback(
    (statusOverride?: ContentStatus): SaveContentBody => {
      if (!resolvedType) {
        throw new Error("missing_content_type");
      }
      const blocks_en = (content?.blocks_en_draft || []) as BlockBase[];
      const blocks_fa = (content?.blocks_fa_draft || []) as BlockBase[];
      const slugEn = toSlug(content?.title_en_draft ?? "", "en");
      const slugFa = toSlug(content?.title_fa_draft ?? "", "fa");
      return {
        id: content?.id || undefined,
        type: resolvedType,
        status: statusOverride ?? (content?.status as ContentStatus) ?? "draft",
        authorFaId: content?.authorFaId ?? null,
        authorEnId: content?.authorEnId ?? null,
        slug_en: slugEn || null,
        slug_fa: slugFa || null,
        title_en: content?.title_en_draft ?? null,
        title_fa: content?.title_fa_draft ?? null,
        blocks_en,
        blocks_fa,
        meta: content?.meta_draft ?? {},
        displayDate: content?.displayDate_draft ?? null,
        readingMinutes:
          typeof content?.readingMinutes_draft === "string"
            ? content?.readingMinutes_draft
            : typeof content?.readingMinutes_draft === "number"
              ? `${content?.readingMinutes_draft}min`
              : null,
        articleColor: content?.articleColor_draft ?? null,
        description_en: content?.description_en_draft ?? null,
        description_fa: content?.description_fa_draft ?? null,
        tagIds: Array.isArray(content?.tags)
          ? (content?.tags as TagOption[]).map((tag) => tag.id)
          : [],
        actorId: actorEmail ?? undefined,
      };
    },
    [actorEmail, content, resolvedType],
  );

  const handleSave = React.useCallback(async () => {
    if (!resolvedType) {
      setError("Content type is missing. Please close and try again.");
      setErrorIssues(null);
      return false;
    }
    setIsSaving(true);
    setError(null);
    setErrorIssues(null);
    try {
      const previousContent = content;
      const res = await fetch("/api/cms/content", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) {
        const message = data.message || data.error || "Unknown error";
        if (Array.isArray(data?.issues) && data.issues.length) {
          setError("Save failed.");
          setErrorIssues(data.issues);
        } else {
          setError(message);
          setErrorIssues(null);
        }
        return false;
      }
      const normalized = normalizeContent(data.content);
      setContent(normalized);
      setIsDirty(false);
      onUpdated?.({ id: data.content.id, data: data.content });
      const usageSyncOk = await syncContentAssetUsages({
        next: normalized,
        prev: previousContent,
      });
      if (!usageSyncOk) {
        setError(
          "Content saved, but media usage records could not be fully updated.",
        );
        setErrorIssues(null);
      }
      return normalized;
    } catch (err: any) {
      const message = err?.message || "Save failed";
      setError(message);
      setErrorIssues(null);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [buildPayload, content, onUpdated, resolvedType, syncContentAssetUsages]);

  const handlePublish = React.useCallback(
    async (force?: boolean, contentOverride?: any) => {
      const contentToPublish = contentOverride ?? content;
      const contentIsPublished = contentToPublish?.status === "published";
      if ((contentIsPublished && !force) || isArchived) return false;
      if (!contentToPublish?.id) {
        setError("Save the content before publishing.");
        setErrorIssues(null);
        return false;
      }
      setError(null);
      setErrorIssues(null);
      setStatusAction("publish");
      try {
        const res = await fetch(`/api/cms/content/${contentToPublish.id}/publish`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ actorId: actorEmail ?? null }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (Array.isArray(data?.issues) && data.issues.length) {
            const message = data.issues
              .map((issue: any) => {
                const prefix = issue.blockId
                  ? `#${issue.blockId} `
                  : issue.field
                    ? `${issue.field} `
                    : "";
                return `${prefix}— ${issue.message}`;
              })
              .join("\n");
            setError(message);
            setErrorIssues(data.issues);
          } else {
            setError(
              "Publish failed: " +
                (data?.message || data?.error || "Unknown error"),
            );
            setErrorIssues(null);
          }
          return false;
        }
        const normalized = normalizeContent(data.content);
        setContent(normalized);
        setIsDirty(false);
        onUpdated?.({ id: data.content.id, data: data.content });
        return true;
      } finally {
        setStatusAction(null);
      }
    },
    [actorEmail, content, isArchived, onUpdated],
  );

  const handleSaveAndPublish = React.useCallback(async () => {
    const saved = await handleSave();
    if (!saved) return false;
    return handlePublish(true, saved);
  }, [handlePublish, handleSave]);

  const handlePublishOriginal = handlePublish;
  // maintain backward compatibility in returned handlers
  const handlePublishWithoutForce = React.useCallback(
    () => handlePublishOriginal(false),
    [handlePublishOriginal],
  );

  const handleUnpublish = React.useCallback(async () => {
    if (!content?.id || !resolvedType) {
      alert("Save the content before updating status.");
      return false;
    }
    setStatusAction("unpublish");
    try {
      const res = await fetch("/api/cms/content", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(buildPayload("draft")),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(
          "Status update failed: " +
            (data?.message || data?.error || "Unknown error"),
        );
        return false;
      }
      setContent(normalizeContent(data.content));
      setIsDirty(false);
      onUpdated?.({ id: data.content.id, data: data.content });
      return true;
    } finally {
      setStatusAction(null);
    }
  }, [buildPayload, content?.id, onUpdated, resolvedType]);

  const handleDiscardDraft = React.useCallback(async () => {
    if (!content?.id) return false;
    setStatusAction("discard");
    try {
      const res = await fetch(`/api/cms/content/${content.id}/discard`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ actorId: actorEmail ?? null }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(
          "Discard failed: " +
            (data?.message || data?.error || "Unknown error"),
        );
        return false;
      }
      const normalized = normalizeContent(data.content);
      setContent(normalized);
      setIsDirty(false);
      onUpdated?.({ id: data.content.id, data: data.content });
      return true;
    } finally {
      setStatusAction(null);
    }
  }, [actorEmail, content?.id, onUpdated]);

  const handlePreview = React.useCallback(
    async (locale: "en" | "fa") => {
      if (!content?.id) {
        alert("Save the content before previewing.");
        return;
      }
      const newWindow = window.open("", "_blank");
      if (!newWindow) {
        console.warn("Popup was blocked by the browser");
        return;
      }
      try {
        const res = await fetch("/api/cms/preview-url", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ contentId: content.id, locale }),
        });
        const data = await res.json();

        if (data?.token) {
          newWindow.location.href = `/${locale}/preview?token=${encodeURIComponent(
            data.token,
          )}`;
        } else {
          newWindow.close();
        }
      } catch (err) {
        newWindow.close();
        console.error(err);
      }
    },
    [content?.id],
  );

  const setTitleEn = React.useCallback((value: string) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        title_en_draft: value,
        slug_en_draft: toSlug(value, "en") || null,
      };
    });
    setIsDirty(true);
  }, []);

  const setTitleFa = React.useCallback((value: string) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        title_fa_draft: value,
        slug_fa_draft: toSlug(value, "fa") || null,
      };
    });
    setIsDirty(true);
  }, []);

  const setBlocksEn = React.useCallback((blocks: BlockBase[]) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return { ...prev, blocks_en_draft: blocks };
    });
    setIsDirty(true);
  }, []);

  const setBlocksFa = React.useCallback((blocks: BlockBase[]) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return { ...prev, blocks_fa_draft: blocks };
    });
    setIsDirty(true);
  }, []);

  const updateMeta = React.useCallback((next: Record<string, any>) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return { ...prev, meta_draft: { ...(prev.meta_draft ?? {}), ...next } };
    });
    setIsDirty(true);
  }, []);

  const updateLocaleVisibility = React.useCallback(
    (locale: "en" | "fa", visible: boolean) => {
      setContent((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          meta_draft: withContentLocaleVisibility(prev.meta_draft ?? {}, {
            [locale]: visible,
          }),
        };
      });
      setIsDirty(true);
    },
    [],
  );

  const updateArticleColor = React.useCallback((next: string | null) => {
    setContent((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        articleColor_draft: next,
      };
    });
    setIsDirty(true);
  }, []);

  const handleCloneContentBlocks = React.useCallback(
    (payload: {
      blocks_en?: BlockBase[];
      blocks_fa?: BlockBase[];
      sourceId?: string;
      sourceTitle?: string;
    }) => {
      const hasEnglish = payload.blocks_en && payload.blocks_en.length > 0;
      const hasFarsi = payload.blocks_fa && payload.blocks_fa.length > 0;
      if (!hasEnglish && !hasFarsi) {
        return;
      }
      setContent((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          blocks_en_draft: hasEnglish
            ? [...(prev?.blocks_en_draft || []), ...payload.blocks_en!]
            : (prev?.blocks_en_draft ?? []),
          blocks_fa_draft: hasFarsi
            ? [...(prev?.blocks_fa_draft || []), ...payload.blocks_fa!]
            : (prev?.blocks_fa_draft ?? []),
        };
      });
      setIsDirty(true);
    },
    [],
  );

  const addTag = React.useCallback(
    (tag: TagOption) => {
      if (isArchived) return;
      let changed = false;
      setContent((prev: any) => {
        if (!prev) return prev;
        const existing: TagOption[] = Array.isArray(prev.tags) ? prev.tags : [];
        if (existing.some((item) => item.id === tag.id)) {
          return prev;
        }
        changed = true;
        return {
          ...prev,
          tags: [...existing, tag],
        };
      });
      if (changed) setIsDirty(true);
    },
    [isArchived],
  );

  const removeTag = React.useCallback(
    (tagId: string) => {
      if (isArchived) return;
      let changed = false;
      setContent((prev: any) => {
        if (!prev) return prev;
        const existing: TagOption[] = Array.isArray(prev.tags) ? prev.tags : [];
        const nextTags = existing.filter((tag) => {
          if (tag.id === tagId) {
            changed = true;
            return false;
          }
          return true;
        });
        if (!changed) return prev;
        return {
          ...prev,
          tags: nextTags,
        };
      });
      if (changed) setIsDirty(true);
    },
    [isArchived],
  );

  const updateDisplayDate = React.useCallback((next: string | null) => {
    if (isArchived) return;
    setContent((prev: any) => ({ ...prev, displayDate_draft: next }));
    setIsDirty(true);
  }, [isArchived]);

  const updateReadingMinutes = React.useCallback(
    (next: string | null) => {
      if (isArchived) return;
      setContent((prev: any) => ({ ...prev, readingMinutes_draft: next }));
      setIsDirty(true);
    },
    [isArchived],
  );

  const updateDescription = React.useCallback(
    (locale: "en" | "fa", next: string | null) => {
      if (isArchived) return;
      setContent((prev: any) => {
        if (!prev) return prev;
        return locale === "fa"
          ? { ...prev, description_fa_draft: next }
          : { ...prev, description_en_draft: next };
      });
      setIsDirty(true);
    },
    [isArchived],
  );

  const setAuthor = React.useCallback(
    (locale: "en" | "fa", author: AuthorSummary | null) => {
      if (isArchived) return;
      setContent((prev: any) => {
        if (!prev) return prev;
        return locale === "fa"
          ? {
              ...prev,
              authorFaId: author?.id ?? null,
              authorFa: author,
            }
          : {
              ...prev,
              authorEnId: author?.id ?? null,
              authorEn: author,
            };
      });
      setIsDirty(true);
    },
    [isArchived],
  );

  const allowedBlockTypes =
    resolvedType && BLOCKS_BY_CONTENT_TYPE[resolvedType]
      ? BLOCKS_BY_CONTENT_TYPE[resolvedType]
      : undefined;

  return {
    data: {
      content,
      contentId: content?.id ?? null,
      type: resolvedType,
      titleEn: content?.title_en_draft ?? "",
      titleFa: content?.title_fa_draft ?? "",
      blocksEn: (content?.blocks_en_draft || []) as BlockBase[],
      blocksFa: (content?.blocks_fa_draft || []) as BlockBase[],
      tags: (content?.tags as TagOption[] | undefined) ?? [],
      status: (content?.status as ContentStatus | undefined) ?? "draft",
      meta: (content?.meta_draft as Record<string, any> | undefined) ?? {},
      displayDate: content?.displayDate_draft ?? null,
      readingMinutes:
        typeof content?.readingMinutes_draft === "string"
          ? content.readingMinutes_draft
          : typeof content?.readingMinutes_draft === "number"
            ? `${content.readingMinutes_draft}min`
            : null,
      articleColor: content?.articleColor_draft ?? null,
      descriptionEn: content?.description_en_draft ?? "",
      descriptionFa: content?.description_fa_draft ?? "",
      authorFaId: content?.authorFaId ?? null,
      authorEnId: content?.authorEnId ?? null,
      authorFa: (content?.authorFa as AuthorSummary | null | undefined) ?? null,
      authorEn: (content?.authorEn as AuthorSummary | null | undefined) ?? null,
      usageMap,
      allowedBlockTypes,
      isPublished,
      isArchived,
    },
    state: {
      isLoading,
      isSaving,
      isPublishing: statusAction === "publish",
      isUpdatingStatus: statusAction !== null,
      isDirty,
      error,
      errorIssues,
      lockInfo: { status: lockStatus, lockedBy, lockBlocked, retry: retryLock },
    },
    handlers: {
      setTitleEn,
      setTitleFa,
      setBlocksEn,
      setBlocksFa,
      updateMeta,
      updateLocaleVisibility,
      updateArticleColor,
      clearError: () => {
        setError(null);
        setErrorIssues(null);
      },
      handleSave,
      handleSaveAndPublish,
      handlePublish: handlePublishWithoutForce,
      handleUnpublish,
      handleDiscardDraft,
      handlePreview,
      handleCloneContentBlocks,
      addTag,
      removeTag,
      updateDisplayDate,
      updateReadingMinutes,
      updateDescription,
      setAuthor,
    },
  };
}
